import { apiClient } from "./client";
import type {
  ConvertDesignPartnerLicenseResponse,
  GetLicenseResponse,
  IssueLicenseRequest,
  IssueLicenseResponse,
  License,
  LicenseStatus,
  LicenseType,
  ListLicensesForOrgResponse,
  RevokeLicenseResponse,
  VerifyLicenseResponse,
} from "./types";

// Mirrors LicenseService RPCs in console/proto/license.proto.

export async function issueLicense(req: IssueLicenseRequest): Promise<IssueLicenseResponse> {
  const { data } = await apiClient.post<IssueLicenseResponse>("/licenses", req);
  return data;
}

export async function getLicense(id: string): Promise<GetLicenseResponse> {
  const { data } = await apiClient.get<GetLicenseResponse>(`/licenses/${id}`);
  return data;
}

export async function listLicensesForOrg(
  organizationId: string,
  params?: { limit?: number; offset?: number },
): Promise<ListLicensesForOrgResponse> {
  const { data } = await apiClient.get<ListLicensesForOrgResponse>(
    `/organizations/${organizationId}/licenses`,
    { params },
  );
  return data;
}

export async function revokeLicense(id: string, reason: string): Promise<RevokeLicenseResponse> {
  const { data } = await apiClient.post<RevokeLicenseResponse>(`/licenses/${id}/revoke`, {
    reason,
  });
  return data;
}

export async function verifyLicense(
  organizationId: string,
  appId: string,
): Promise<VerifyLicenseResponse> {
  const { data } = await apiClient.get<VerifyLicenseResponse>("/licenses/verify", {
    params: { organization_id: organizationId, app_id: appId },
  });
  return data;
}

export async function convertDesignPartnerLicense(
  licenseId: string,
  discountPercent: number,
): Promise<ConvertDesignPartnerLicenseResponse> {
  const { data } = await apiClient.post<ConvertDesignPartnerLicenseResponse>(
    `/licenses/${licenseId}/convert-design-partner`,
    { discount_percent: discountPercent },
  );
  return data;
}

export const LICENSE_STATUS_LABELS: Record<LicenseStatus, string> = {
  LICENSE_STATUS_UNSPECIFIED: "Unknown",
  LICENSE_STATUS_ACTIVE: "Active",
  LICENSE_STATUS_TRIALING: "Trialing",
  LICENSE_STATUS_SUSPENDED: "Suspended",
  LICENSE_STATUS_CANCELED: "Canceled",
};

export const LICENSE_TYPE_LABELS: Record<LicenseType, string> = {
  LICENSE_TYPE_UNSPECIFIED: "Unknown",
  LICENSE_TYPE_STANDARD: "Standard",
  LICENSE_TYPE_DESIGN_PARTNER: "Design Partner",
};

export function licenseBadgeVariant(
  status: LicenseStatus,
): "success" | "info" | "danger" | "warning" | "default" {
  switch (status) {
    case "LICENSE_STATUS_ACTIVE":
      return "success";
    case "LICENSE_STATUS_TRIALING":
      return "info";
    case "LICENSE_STATUS_SUSPENDED":
      return "warning";
    case "LICENSE_STATUS_CANCELED":
      return "danger";
    default:
      return "default";
  }
}

export function isDesignPartner(license: License): boolean {
  return license.licenseType === "LICENSE_TYPE_DESIGN_PARTNER";
}

// Entitlement view (GET /organizations/{id}/entitlements): license terms and
// the heartbeat state of each deployed instance. Metadata only. Hand-written
// endpoint, snake_case keys.
export type InstanceState = "online" | "stale" | "offline" | "never_seen" | "revoked";

export interface EntitlementInstance {
  id: string;
  name: string;
  state: InstanceState;
  issued_at: string;
  last_seen_at?: string;
  last_version: string;
}

export interface Entitlement {
  license_id: string;
  app: { id: string; slug?: string; name?: string };
  tier: string;
  license_type: string;
  status: string;
  effective_status: string;
  valid_until: string | null;
  seats: number | null;
  instances: EntitlementInstance[];
}

export async function getEntitlements(organizationId: string): Promise<Entitlement[]> {
  const { data } = await apiClient.get<{ entitlements: Entitlement[] }>(
    `/organizations/${organizationId}/entitlements`,
  );
  return data.entitlements;
}

// Usage the deployed apps report (GET /organizations/{id}/usage, ADR-017): aggregate
// counters only. Totals are the latest value of each metric summed over live instances.
export interface UsageApp {
  license_id: string;
  app: { id: string; slug?: string; name?: string };
  totals: Record<string, number>;
  instances: {
    instance_id: string;
    name: string;
    revoked: boolean;
    metrics: Record<string, { day: string; value: number }[]>;
  }[];
}

export async function getUsage(organizationId: string, days = 30): Promise<UsageApp[]> {
  const { data } = await apiClient.get<{ apps: UsageApp[] }>(
    `/organizations/${organizationId}/usage`,
    { params: { days } },
  );
  return data.apps;
}

// Private-deployment instance tokens (hand-written endpoints, snake_case keys,
// console/cmd/console-server/license_token_handlers.go). All three are
// platform-admin only on the server.
export interface LicenseInstance {
  id: string;
  name: string;
  issued_at: string;
  last_seen_at?: string;
  last_version: string;
  revoked: boolean;
}

export interface IssuedInstanceToken {
  token: string;
  expires_at: string;
  instance_id: string;
}

export async function listLicenseInstances(licenseId: string): Promise<LicenseInstance[]> {
  const { data } = await apiClient.get<{ instances: LicenseInstance[] }>(
    `/licenses/${encodeURIComponent(licenseId)}/instances`,
  );
  return data.instances;
}

// The token is returned once (Cache-Control: no-store) and is never stored by
// the server. Callers must keep it in component state only: not in the query
// cache (so call this directly, not through useMutation), storage or the URL.
export async function issueInstanceToken(
  licenseId: string,
  instanceName: string,
): Promise<IssuedInstanceToken> {
  const { data } = await apiClient.post<IssuedInstanceToken>(
    `/licenses/${encodeURIComponent(licenseId)}/token`,
    { instance_name: instanceName },
  );
  return { token: data.token, expires_at: data.expires_at, instance_id: data.instance_id };
}

export async function revokeLicenseInstance(licenseId: string, instanceId: string): Promise<void> {
  await apiClient.post(
    `/licenses/${encodeURIComponent(licenseId)}/instances/${encodeURIComponent(instanceId)}/revoke`,
  );
}
